import './styles/theme.css';
import { useEffect, useState, type JSX } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { api } from './api/client.js';
import { ensureSignedIn, signIn } from './auth/authService.js';
import { authConfigured } from './auth/msal.js';
import { AppProvider, useApp } from './state/app.js';
import { Shell } from './layout/Shell.js';
import { Home } from './screens/Home.js';
import { Board } from './screens/Board.js';
import { Leave } from './screens/Leave.js';
import { Trip } from './screens/Trip.js';
import { CompanyInfo } from './screens/CompanyInfo.js';
import { CompanyCI } from './screens/CompanyCI.js';
import { Resources } from './screens/Resources.js';
import { Library } from './screens/Library.js';
import { Weekly } from './screens/Weekly.js';
import { Expense } from './screens/Expense.js';
import { Overtime } from './screens/Overtime.js';
import { RandomPicker } from './screens/RandomPicker.js';
import { Admin } from './screens/Admin.js';
import { Approve } from './screens/Approve.js';
import { Stats } from './screens/Stats.js';
import { PeopleStats } from './screens/PeopleStats.js';
import { KudosScreen } from './screens/Kudos.js';
import { Assets } from './screens/Assets.js';
import { Meals } from './screens/Meals.js';
import { Settings } from './screens/Settings.js';
import { Spinner, Toast } from './ui/common.js';
import { MsLogo } from './ui/icons.js';
import { VERSION_LABEL } from './buildInfo.js';

const qc = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });

export function App(): JSX.Element {
  return (
    <QueryClientProvider client={qc}>
      <Root />
    </QueryClientProvider>
  );
}

function Root(): JSX.Element {
  const [phase, setPhase] = useState<'booting' | 'login' | 'app'>('booting');
  useEffect(() => {
    ensureSignedIn().then((ok) => setPhase(ok ? 'app' : 'login')).catch(() => setPhase('login'));
  }, []);

  if (phase === 'app') return <AppShell />;
  return <LoginCard booting={phase === 'booting'} onSignedIn={() => setPhase('app')} />;
}

function LoginCard({ booting, onSignedIn }: { booting: boolean; onSignedIn?: () => void }): JSX.Element {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, background: '#E9EBEF' }}>
      <div style={{ width: 380, maxWidth: '100%', background: '#fff', borderRadius: 16, boxShadow: '0 16px 48px rgba(16,24,40,.16)', padding: '44px 40px', textAlign: 'center', animation: 'dxpop .4s ease' }}>
        <img src="/brand/DXTECH_MARK.svg" alt="DXTECH" style={{ width: 58, height: 58, margin: '0 auto 20px', display: 'block' }} />
        <div style={{ fontSize: 23, fontWeight: 700, letterSpacing: '-.02em' }}>DXTECH Works</div>
        <div style={{ fontSize: 14, color: '#6B7280', marginTop: 7, marginBottom: 34 }}>사내 그룹웨어에 오신 것을 환영합니다</div>
        {booting ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <Spinner />
            <div style={{ fontSize: 13, color: '#6B7280' }}>Microsoft 365 계정 확인 중…</div>
          </div>
        ) : (
          <>
            <button onClick={() => { void signIn().then((ok) => { if (ok) onSignedIn?.(); }); }} className="dx-btn dx-btn-ghost" style={{ width: '100%', padding: 12, gap: 11 }}>
              <MsLogo />Microsoft 계정으로 계속
            </button>
            <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 18, lineHeight: 1.5 }}>
              회사 Microsoft 365 계정으로 로그인됩니다.<br />계정이 없으면 IT 관리자에게 문의하세요.
              {!authConfigured && <><br /><b>(개발 모드: 인증 미구성)</b></>}
            </div>
          </>
        )}
        <div style={{ marginTop: 22, fontSize: 11, color: '#C0C4CB' }}>{VERSION_LABEL}</div>
      </div>
    </div>
  );
}

function AppShell(): JSX.Element {
  const me = useQuery({ queryKey: ['me'], queryFn: api.me });
  if (me.isLoading) return <LoginCard booting />;
  if (me.isError || !me.data) {
    return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280' }}>프로필을 불러오지 못했습니다.</div>;
  }
  return (
    <AppProvider me={me.data}>
      <Inner />
    </AppProvider>
  );
}

function ScreenView(): JSX.Element {
  const { screen } = useApp();
  switch (screen) {
    case 'home': return <Home />;
    case 'board': return <Board />;
    case 'leave': return <Leave />;
    case 'trip': return <Trip />;
    case 'overtime': return <Overtime />;
    case 'weekly': return <Weekly />;
    case 'expense': return <Expense />;
    case 'company': return <CompanyInfo />;
    case 'ci': return <CompanyCI />;
    case 'resources': return <Resources />;
    case 'library': return <Library />;
    case 'picker': return <RandomPicker />;
    case 'admin': return <Admin />;
    case 'approve': return <Approve />;
    case 'stats': return <Stats />;
    case 'peopleStats': return <PeopleStats />;
    case 'kudos': return <KudosScreen />;
    case 'assets': return <Assets />;
    case 'meals': return <Meals />;
    case 'settings': return <Settings />;
    default: return <Home />;
  }
}

function Inner(): JSX.Element {
  const { me, toast, clearToast } = useApp();
  const pending = useQuery({
    queryKey: ['leave', 'pending'],
    queryFn: () => api.leavePending(),
    enabled: me.permissions.canApproveLeave,
  });
  const pendingCount = pending.data?.items.length ?? 0;

  return (
    <>
      <Shell pendingCount={pendingCount}>
        <ScreenView />
      </Shell>
      <Toast message={toast} onDone={clearToast} />
    </>
  );
}
